import { useContext, useState } from "react";
import { EditIcon } from "lucide-react";
import { useForm } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import InputDate from "@/pages/components/input-date";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/pages/components/input-phone";
import { InputWithLabel } from "@/pages/components/input-label";
import { registerPageSchema } from "@/lib/form-schema";
import {
  EditAccountContext,
  EditAccountViewState,
} from "@/pages/containers/editAccount-container";
import useUserInfo from "@/hooks/use-user-info";
import { AuthMode } from "@/context/auth-provider";
import { EMPTY_VALUE } from "@/constants";
import { lowerCaseComparison } from "@/lib/string-util";
import { cn } from "@/lib/utils";
import TextAreaInput from "../input-textarea";

type AccountFormActionsProps = {
  layout: "inline" | "sticky";
  onCancel: () => void;
  saveDisabled: boolean;
};

function AccountFormActions({
  layout,
  onCancel,
  saveDisabled,
}: AccountFormActionsProps) {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2",
        layout === "inline" && "pt-3",
        layout === "sticky" &&
          "flex-shrink-0 border-t border-border bg-background py-3",
      )}
    >
      <Button
        type="button"
        variant="outline"
        className="w-full sm:w-auto"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        className="w-full sm:w-auto"
        disabled={saveDisabled}
      >
        Save Changes
      </Button>
    </div>
  );
}

const EditAccountEnable = () => {
  const [isEditable, setIsEditable] = useState(false);
  const { setViewState, onOpenChange, setPayloadData } =
    useContext(EditAccountContext);

  const userInfo = useUserInfo();

  const form = useForm({
    defaultValues: {
      ...userInfo,
      email: userInfo?.email || EMPTY_VALUE,
      phone: userInfo?.phone || EMPTY_VALUE,
    },
    onSubmit: async ({ value }) => {
      setPayloadData({ ...value });
      form.reset();
      setViewState(EditAccountViewState.VERIFY_PASSWORD);
    },
  });

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col sm:px-1">
      <form
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isEditable) return;
          form.handleSubmit();
        }}
      >
        <form.Subscribe
          selector={(state) => [state.isValid, state.values, state.errors]}
        >
          {([isValid, canSubmit]) => {
            const isUnchanged =
              form.state.values.email === userInfo.email &&
              form.state.values.phone === userInfo.phone &&
              form.state.values.firstname === userInfo.firstname &&
              form.state.values.lastname === userInfo.lastname &&
              form.state.values.mbrStreet1 === userInfo.mbrStreet1 &&
              form.state.values.mbrStreet2 === userInfo.mbrStreet2;

            const saveDisabled =
              !isEditable || isUnchanged || !isValid || !canSubmit;

            return (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {/* Title + edit: fixed at top — does not scroll */}
                <div className="flex flex-shrink-0 flex-row flex-wrap items-center gap-2 border-b border-border pb-3 sm:gap-4 sm:pb-4">
                  <h3 className="text-base font-medium sm:text-lg">
                    Account Details
                  </h3>
                  {lowerCaseComparison(userInfo?.mode, AuthMode.MEMBER) && (
                    <Button
                      variant="link"
                      aria-label="Edit Account Details"
                      className="h-auto w-fit p-0 text-black"
                      onClick={() => setIsEditable(!isEditable)}
                    >
                      <EditIcon
                        size={18}
                        className={`cursor-pointer ${isEditable ? "text-blue-500" : ""}`}
                      />
                    </Button>
                  )}
                </div>

                {/* Scroll area: shrinks when edit footer is visible so content is not covered */}
                <div className="scrollbar-no-arrows min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
                  <div className="flex flex-col gap-4 py-1 sm:gap-5 sm:pt-0">
                    <div className="rounded-md bg-white p-2 sm:p-3">
                      <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-4">
                          <div className="min-w-0 flex-1">
                            <form.Field name="firstname">
                              {(field) => (
                                <div className="flex flex-row items-center space-x-2">
                                  <InputWithLabel
                                    id="firstName"
                                    label="First Name"
                                    placeholder="First Name"
                                    className="truncate"
                                    value={field.state.value || EMPTY_VALUE}
                                    isDisabled
                                  />
                                </div>
                              )}
                            </form.Field>
                          </div>

                          <div className="min-w-0 flex-1">
                            <form.Field name="lastname">
                              {(field) => (
                                <div className="flex flex-row items-center space-x-2">
                                  <InputWithLabel
                                    id="lastName"
                                    label="Last Name"
                                    placeholder="Last Name"
                                    className="truncate"
                                    value={field.state.value || EMPTY_VALUE}
                                    isDisabled
                                  />
                                </div>
                              )}
                            </form.Field>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <form.Field
                            name="email"
                            validators={{
                              onBlur: ({ value }) => {
                                const result =
                                  registerPageSchema.shape.email.safeParse(
                                    value,
                                  );
                                return result.success
                                  ? undefined
                                  : result.error.errors[0].message;
                              },
                            }}
                          >
                            {(field) => (
                              <div className="flex flex-row items-center space-x-2">
                                <InputWithLabel
                                  id="email"
                                  label="Email Address"
                                  placeholder="Email Address"
                                  className="truncate"
                                  value={field.state.value}
                                  onChange={(newValue) =>
                                    field.handleChange(newValue)
                                  }
                                  onBlur={() => field.handleBlur()}
                                  error={field.state.meta.errors[0]}
                                  isDisabled={!isEditable}
                                />
                              </div>
                            )}
                          </form.Field>
                        </div>

                        <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
                          <div className="flex min-w-0 flex-col sm:max-w-[280px]">
                            <form.Field
                              name="phone"
                              validators={{
                                onBlur: ({ value }) => {
                                  const result =
                                    registerPageSchema.shape.phone.safeParse(
                                      value,
                                    );
                                  return result.success
                                    ? undefined
                                    : result.error.errors[0].message;
                                },
                              }}
                            >
                              {(field) => (
                                <div className="flex w-full flex-col gap-3">
                                  {/* 3.3.2 Labels or Instructions — A: the <Label> had no
                                      htmlFor and the field no id, so the label was
                                      orphaned and the phone field had no accessible
                                      name. */}
                                  <Label htmlFor="account-phone-number">
                                    Phone Number
                                  </Label>
                                  <PhoneInput
                                    id="account-phone-number"
                                    className="w-full text-sm"
                                    defaultCountry="US"
                                    value={field.state.value}
                                    onChange={(newValue) =>
                                      field.handleChange(newValue)
                                    }
                                    onBlur={() => field.handleBlur()}
                                    error={field.state.meta.errors[0]}
                                    disabled={!isEditable}
                                  />
                                </div>
                              )}
                            </form.Field>
                          </div>

                          <div className="min-w-0 flex-1 space-y-1">
                            <InputDate
                              id="dob"
                              label="Date of Birth"
                              placeholder="DOB"
                              value={new Date(userInfo?.dob)}
                              disabled
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <form.Field name="mbrStreet1">
                            {(field) => (
                              <div className="flex flex-row items-center space-x-2">
                                <div className="flex w-full flex-col gap-3">
                                  <Label>Street Address 1</Label>
                                  <TextAreaInput
                                    id="streetAddress1"
                                    placeholder="Street Address 1"
                                    value={field.state.value || EMPTY_VALUE}
                                    className="h-11 w-full resize-none overflow-hidden break-all border-none bg-gray-100 pr-0"
                                    autoResize
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                          </form.Field>
                        </div>

                        <div className="space-y-1">
                          <form.Field name="mbrStreet2">
                            {(field) => (
                              <div className="flex flex-row items-center space-x-2">
                                <div className="flex w-full flex-col gap-3">
                                  <Label>Street Address 2</Label>
                                  <TextAreaInput
                                    id="streetAddress2"
                                    placeholder="Street Address 2"
                                    value={field.state.value || EMPTY_VALUE}
                                    className="h-11 w-full resize-none overflow-hidden break-all border-none bg-gray-100 pr-0"
                                    autoResize
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                          </form.Field>
                        </div>

                        <div className="space-y-1">
                          <InputWithLabel
                            label="City"
                            className="truncate"
                            value={userInfo?.city || EMPTY_VALUE}
                            id="city"
                            isDisabled
                          />
                        </div>

                        <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
                          <div className="min-w-0 flex-[2]">
                            <InputWithLabel
                              label="State"
                              value={userInfo?.state || EMPTY_VALUE}
                              id="state"
                              isDisabled
                            />
                          </div>

                          <div className="flex min-w-0 flex-1 flex-col">
                            <InputWithLabel
                              label="Zip Code"
                              className="truncate"
                              value={userInfo?.zipCode || EMPTY_VALUE}
                              id="zip-code"
                              isDisabled
                            />
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-full min-w-0">
                            <InputWithLabel
                              label="Group Name"
                              className="truncate sm:whitespace-nowrap sm:pr-3"
                              value={userInfo?.divisionName || EMPTY_VALUE}
                              id="group-name"
                              isDisabled
                            />
                          </div>
                        </div>

                        {!isEditable && (
                          <AccountFormActions
                            layout="inline"
                            onCancel={() => onOpenChange(false)}
                            saveDisabled={saveDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {isEditable && (
                  <AccountFormActions
                    layout="sticky"
                    onCancel={() => {
                      setIsEditable(false);
                      form.reset();
                      onOpenChange(false);
                    }}
                    saveDisabled={saveDisabled}
                  />
                )}
              </div>
            );
          }}
        </form.Subscribe>
      </form>
    </div>
  );
};

export default EditAccountEnable;
